'use client';

import { useMemo } from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  content: string;
  title?: string;
}

/**
 * TableOfContents - Generates a table of contents from markdown headings.
 * Extracts H2 and H3 headings from raw markdown content.
 */
export function TableOfContents({
  content,
  title = 'Table of Contents',
}: TableOfContentsProps) {
  const headings = useMemo(() => extractHeadings(content), [content]);

  if (headings.length < 3) {
    return null;
  }

  return (
    <Box
      component="nav"
      aria-label="Table of contents"
      sx={{
        bgcolor: 'grey.50',
        borderRadius: 2,
        p: 2.5,
        mb: 4,
        border: 1,
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="subtitle1"
        component="h2"
        sx={{ fontWeight: 600, mb: 1.5 }}
      >
        {title}
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {headings.map((heading) => (
          <Box
            component="li"
            key={heading.id}
            sx={{
              pl: heading.level === 3 ? 2 : 0,
              py: 0.4,
            }}
          >
            <MuiLink
              href={`#${heading.id}`}
              underline="hover"
              color="text.secondary"
              sx={{
                fontSize: heading.level === 3 ? '0.9rem' : '0.95rem',
                fontWeight: heading.level === 2 ? 500 : 400,
                '&:hover': { color: 'primary.main' },
              }}
            >
              {heading.text}
            </MuiLink>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function extractHeadings(markdown: string): TocItem[] {
  const headingRegex = /^(#{2,3})\s+(.+)$/gm;
  const headings: TocItem[] = [];
  const idCounts = new Map<string, number>();
  let match;

  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1]!.length;
    const text = match[2]!.trim();
    let id = text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}]+/gu, '-')
      .replace(/^-|-$/g, '');

    if (!id) {
      id = `heading-${headings.length}`;
    }

    const count = idCounts.get(id) ?? 0;
    idCounts.set(id, count + 1);
    if (count > 0) {
      id = `${id}-${count}`;
    }

    headings.push({ id, text, level });
  }

  return headings;
}
